import Vue from 'vue';
import groupBy from 'lodash/groupBy';

import { Component, Prop } from 'vue-property-decorator';
import { SelectableMenuItem, Menu, SelectableVariantMenuItem, VariantMenuItem, SelectableMenuItemSource, PortionItemType } from '@/models';
import { joinWithSuffix } from '@/helpers/formatting';
import { findItemInMenu, UniqueValue } from '@/helpers';

@Component({
  name: 'CmgMealBuilderFooter',
})
export default class CmgMealBuilderFooter extends Vue {
  @Prop({ required: true, type: Array })
  selectedItems!: SelectableMenuItem[];

  @Prop({ type: Object })
  validationError?: UniqueValue<string>;

  @Prop({ default: false, type: Boolean })
  forceValidationErrorOpen!: boolean;

  @Prop({ required: true, type: String })
  addToBagButtonText!: string;

  @Prop({ default: false, type: Boolean })
  loading!: boolean;

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get isRestaurantSelected(): boolean {
    return !!this.$appStore.state.restaurant.selectedRestaurant;
  }

  get bagVisible(): boolean {
    return this.$appStore.state.ui.bag.visible;
  }

  get mealDescription(): string {
    const mode = this.$appStore.state.mealBuilder.builderMode;
    if (!mode) {
      return '';
    }
    
    const title = this.$services.mealBuilder.getVariantsItemSectionTitle(mode).toLowerCase();
    // Split on the pipe character for variants with piped titles e.g. BYO 'Protein | Choose One
    const emptyMealDescription = `Select ` + (title ? 'a ' + title.split(' | ')[0] : 'an item') +  ` to get started`;

    if (!this.selectedItems.length || !this.menu) {
      return emptyMealDescription;
    }

    const groupedItems = groupBy(this.selectedItems, i => i.itemSource);
    const metadata = this.$appStore.state.menu.metadata;

    const groupedNonVariantItemCategories = groupBy(
      (groupedItems[SelectableMenuItemSource.NonVariant] || []).map(i => ({ name: metadata!.nonVariantSectionMap[i.item.itemId], quantity: this.$services.mealBuilder.getEffectiveItemQuantity(i) })),
      i => i.name,
    );

    const formattedVariants = (groupedItems[SelectableMenuItemSource.Variant] || []).map(item => this.formatVariantName(this.menu!, item as SelectableVariantMenuItem));

    const formattedNonVariants = Object.entries(groupedNonVariantItemCategories).map(([name, values]) => {
      const quantity = values.reduce((acc, i) => i.quantity + acc, 0);

      return `${quantity} ${name}${quantity == 1 ? '' : 's'}`;
    });

    const formattedItems = [...formattedVariants, ...formattedNonVariants];
    if (!formattedItems.length) {
      return emptyMealDescription;
    }

    return joinWithSuffix(formattedItems, ', ', ', ');
  }

  onClickAddToBag(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$emit('add-to-bag');
  }

  onClickFindAChipotle() {
      this.$services.ui.setModalVisibility('findAChipotle', true);
  }

  private formatVariantName(menu: Menu, variant: SelectableVariantMenuItem): string {
    const portions = this.$services.mealBuilder.getSelectedItems(variant.portions || []);
    if (!portions.length) {
      return variant.item.itemName;
    }

    const portionNames = Object.entries(
      portions.reduce(
        (acc, p) => {
          const referencedItemId = p.item.pricingReferenceItemId;
          const referencedItem = referencedItemId ? (findItemInMenu(referencedItemId, menu) as VariantMenuItem) : undefined;
          const name = referencedItem && referencedItem.primaryFillingName;

          if (name) {
            acc[name] = (acc[name] || 0) + (p.item.itemType == PortionItemType.ExtraPortion ? 1 : 0.5);
          }

          return acc;
        },
        { [variant.item.primaryFillingName]: 1 } as { [key: string]: number | undefined },
      ),
    ).map(([name, count]) => {
      switch (count) {
        case 0.5:
        case 1:
          return name;
        case 2:
          return `Double ${name}`;
        default:
          return `${count}x ${name}`;
      }
    });

    return `${joinWithSuffix(portionNames, ' and ', ' & ')} ${variant.item.itemType}`;
  }
}
